import type BusinessRule from '~/types/BusinessRule';
import type ParcelTemplate from '~/types/ParcelTemplate';

import {
  ShippingDestinationDomesticOption,
  ShippingDestinationInternationalOption,
} from '~/components/Select/options';
import { ActionType, ConditionFilterType } from '~/constants/automationRuleForm';
import { countries } from '~/constants/countries';
import { OperatorString } from '~/constants/operators';
import { ruleReprocessingStatus } from '~/constants/rules';
import * as apiAddress from '~/types/APIAddress';
import { getCountryStates, getStateOptions } from '~/types/Address';
import {
  type Filter,
  type FilterClause,
  type FilterGroup,
  type FilterOrClause,
  type FilterValue,
  type JSONBusinessRule,
  type JSONFilter,
  type JSONFilterClause,
  type JSONFilterGroup,
  type JSONFilterOrClause,
  type ReorderRulePayload,
} from '~/types/BusinessRule';
import { isFilter } from '~/types/BusinessRule';
import { type OrderTag } from '~/types/Order';
import { fromJSON } from '~/types/ParcelTemplate';
import { type MappedServiceLevelsToken } from '~/types/ServiceLevel';

export type ParseBusinessRulesReturnType = {
  associatedPackageTemplateIds: string[];
  businessRules: BusinessRule[];
};

enum RuleTypes {
  Insurance = 'INSURANCE',
  ParcelTemplate = 'PARCEL_TEMPLATE',
}

// We don't show the rules anywhere in the UI with no name
// This method filter out the rules with no names
// https://shippo.atlassian.net/browse/ENG-30263

export const filterRulesToShow = (rules?: BusinessRule[]): BusinessRule[] => {
  if (!rules) return [];
  return rules?.filter((rule) => rule?.name);
};

export const parseBusinessRules = (rules: JSONBusinessRule[]): ParseBusinessRulesReturnType => {
  const associatedPackageTemplateIds: string[] = [];
  const businessRules = rules.map((rule) => {
    const businessRule: BusinessRule = {
      accountObjectId: rule.account_object_id,
      description: rule.description,
      name: rule.name,
      objectId: rule.object_id,
      objectUpdated: new Date(rule.object_updated),
      rankDesc: rule.rank_desc,
      ruleType: rule.rule_type,
      senderAPIAddress: apiAddress.fromJSON(rule.sender_address) || undefined,
      serviceLevelToken: rule.service_level_token,
      tagId: rule.tag_id,
      userParcelTemplate: fromJSON(rule.user_parcel_template) as ParcelTemplate,
    };

    if (rule.filter_group) saveFilterGroupData(businessRule, rule.filter_group);

    setBusinessRuleNameAndDescription(businessRule);

    if (rule.user_parcel_template)
      associatedPackageTemplateIds.push(rule.user_parcel_template.object_id);

    return businessRule;
  });

  return { associatedPackageTemplateIds, businessRules };
};

const comparatorMap = {
  [OperatorString.ENDS_WITH]: {
    symbol: 'ends with',
    word: 'ends with',
  },
  [OperatorString.EQ]: {
    symbol: '=',
    word: 'is equal to',
  },
  [OperatorString.GT]: {
    symbol: '>',
    word: 'is greater than',
  },
  [OperatorString.GTE]: {
    symbol: '>=',
    word: 'is greater than or equal to',
  },
  [OperatorString.IN]: {
    symbol: 'in', // no symbol for in
    word: 'is in',
  },
  [OperatorString.INCLUDES]: {
    symbol: 'includes',
    word: 'includes',
  },
  [OperatorString.LT]: {
    symbol: '<',
    word: 'is less than',
  },
  [OperatorString.LTE]: {
    symbol: '<=',
    word: 'is less than or equal to',
  },
  [OperatorString.NEQ]: {
    symbol: '!=',
    word: 'is not equal to',
  },
  [OperatorString.STARTS_WITH]: {
    symbol: 'starts with',
    word: 'starts with',
  },
};

const parseFilterOrClauseData = (jsonFilterOrClause: JSONFilterOrClause): FilterOrClause => {
  if (isFilter(jsonFilterOrClause)) return parseFilterData(jsonFilterOrClause as JSONFilter);
  else return parseFilterClauseData(jsonFilterOrClause as JSONFilterClause);
};

const parseFilterClauseData = (jsonFilterClause: JSONFilterClause): FilterClause => {
  const subfilters = jsonFilterClause.filters.map(parseFilterOrClauseData);
  const filterClause: FilterClause = {
    filters: subfilters,
    operator: jsonFilterClause.operator,
  };
  return filterClause;
};

const parseFilterData = (jsonFilter: JSONFilter): Filter => {
  const filterValue: FilterValue = jsonFilter.filter_values[0];
  // Maps is_international filter field to UI Shipping destination field
  if (jsonFilter.field === ConditionFilterType.IS_INTERNATIONAL) {
    jsonFilter.field = ConditionFilterType.SHIPPING_DESTINATION;
    filterValue.text = filterValue.boolean
      ? ShippingDestinationInternationalOption.value.toString()
      : ShippingDestinationDomesticOption.value.toString();
  }

  const value = setValueByFilterType(jsonFilter.field as ConditionFilterType, filterValue);

  const filter: Filter = {
    comparator: jsonFilter.comparator,
    comparatorString: comparatorMap[jsonFilter.comparator].word,
    field: ConditionFilterType[jsonFilter.field as keyof typeof ConditionFilterType],
    filterValues: jsonFilter.filter_values,
    id: jsonFilter.id,
    symbolString: `${comparatorMap[jsonFilter.comparator].symbol} ${value}`,
    value,
    wordString: `${comparatorMap[jsonFilter.comparator].word} ${value}`,
  };
  return filter;
};

const saveFilterGroupData = (rule: BusinessRule, filterGroup: JSONFilterGroup) => {
  // transform JSONFilterOrClause[] to FilterOrClause[]
  const filters = filterGroup?.filters?.map(parseFilterOrClauseData) || [];

  // then use in FilterGroup and add that to the rule
  const fg: FilterGroup = {
    filterEntity: filterGroup.filter_entity,
    filters,
    id: filterGroup.id,
    objectId: filterGroup.object_id,
  };

  rule.filterGroup = fg;
};

const filterTypeProperties = {
  [ConditionFilterType.IS_INTERNATIONAL]: {
    name: 'Shipping destination',
    prefix: 'Shipping destination is',
  },
  [ConditionFilterType.ITEM_SKU]: {
    name: 'Item SKU',
    prefix: 'Item SKU',
  },
  [ConditionFilterType.ORDER_ITEM_QUANTITY]: {
    name: 'Order item quantity',
    prefix: 'Order item quantity',
  },
  [ConditionFilterType.PRODUCT_NAME]: {
    name: 'Product name',
    prefix: 'Product name',
  },
  [ConditionFilterType.SHIPPING_COUNTRIES]: {
    name: 'Shipping countries',
    prefix: 'Shipping to countries including',
  },
  [ConditionFilterType.SHIPPING_DESTINATION]: {
    name: 'Shipping destination',
    prefix: 'Shipping destination is',
  },
  [ConditionFilterType.SHIPPING_STATES]: {
    name: 'Shipping states',
    prefix: 'Shipping to states in',
  },
  [ConditionFilterType.SHIPPING_ZIPCODES]: {
    name: 'Shipping zip codes',
    prefix: 'Shipping to zip codes in',
  },
  [ConditionFilterType.WEIGHT]: {
    name: 'Order weight',
    prefix: 'Order weight',
  },
};

/**
 * Given a filter field type and property name, returns the value of the desired property if present
 * @param filterType The input filter to find the property for
 * @param propertyName Currently ("name" | "prefix")
 */
const getFilterTypeProperty = (filterType: string, propertyName: string) => {
  if (filterTypeProperties[filterType as keyof typeof filterTypeProperties]) {
    const properties = filterTypeProperties[filterType as keyof typeof filterTypeProperties];
    return properties[propertyName as keyof typeof properties];
  }
};

/**
 * @return string value based on filter type
 */
const setValueByFilterType = (
  filterType: ConditionFilterType,
  filterValue: FilterValue,
): string => {
  const {
    ITEM_SKU,
    ORDER_ITEM_QUANTITY,
    PRODUCT_NAME,
    SHIPPING_COUNTRIES,
    SHIPPING_DESTINATION,
    SHIPPING_STATES,
    SHIPPING_ZIPCODES,
    WEIGHT,
  } = ConditionFilterType;

  // grouped by filter types, add filter type to group based on required return value format.
  const VALUE_ENUM_FILTER_GROUP = [WEIGHT];
  const QUOTE_TEXT_FILTER_GROUP = [PRODUCT_NAME, ITEM_SKU];
  const VALUE_FILTER_GROUP = [ORDER_ITEM_QUANTITY];
  const TEXT_FILTER_GROUP = [
    SHIPPING_DESTINATION,
    SHIPPING_STATES,
    SHIPPING_ZIPCODES,
    SHIPPING_COUNTRIES,
  ];

  // value with metric. ex: 5 lbs
  if (VALUE_ENUM_FILTER_GROUP.includes(filterType))
    return `${filterValue.value} ${filterValue.enum}s`;

  // wrap text with qoutes. ex: "shirt"
  if (QUOTE_TEXT_FILTER_GROUP.includes(filterType)) return `"${filterValue.text}"`;

  // raw value, can be string or number. ex: shirt
  if (VALUE_FILTER_GROUP.includes(filterType)) return `${filterValue.value}`;

  // raw text, string.
  if (TEXT_FILTER_GROUP.includes(filterType)) return `${filterValue.text}`;

  return '';
};

// recursively builds the filter name from the Filters and FilterClauses
const getName = (filterOrClause: FilterOrClause): string => {
  if (isFilter(filterOrClause)) {
    const filter: Filter = filterOrClause as Filter;
    const namePrefix = getFilterTypeProperty(filter.field, 'name');
    return `${namePrefix} ${filter.symbolString}`;
  } else {
    const filterClause: FilterClause = filterOrClause as FilterClause;
    const subfilters = filterClause.filters.map(getName);
    return `(${subfilters.join(' ' + filterClause.operator + ' ')})`;
  }
};

const parseSerializedFilterValue = (filterValue: string) => {
  let parsedFilterValue, country;
  try {
    parsedFilterValue = JSON.parse(filterValue);
    country = countries[parsedFilterValue?.country as keyof typeof countries].name;
  } catch (err) {
    return { country, parsedFilterValue };
  }
  return { country, parsedFilterValue };
};

export const formatDescriptionByFieldType = (filter: Filter) => {
  const namePrefix = getFilterTypeProperty(filter.field, 'prefix');
  let parsedFV;

  switch (filter.field) {
    case 'SHIPPING_STATES':
      parsedFV = parseSerializedFilterValue(filter.value);
      let states: string;
      if (Array.isArray(parsedFV.parsedFilterValue?.states)) {
        states = parsedFV.parsedFilterValue.states.reduce(
          (acc: string, current: { label: string }) => `${acc}${acc ? ',' : ''} ${current.label}`,
          '',
        );
      } else {
        const countryIso = countries[parsedFV.parsedFilterValue?.country];
        const countryStates = getCountryStates(countryIso) || [];
        const stateOptions = countryStates.length > 0 ? getStateOptions(countryStates) : [];
        states =
          parsedFV.parsedFilterValue?.states && parsedFV.parsedFilterValue.states.length > 0
            ? parsedFV.parsedFilterValue.states
                .split(',')
                ?.map((state) => {
                  const stateOption = stateOptions.find(
                    (stateOption) => stateOption?.value === state,
                  );
                  return stateOption?.label || state;
                })
                ?.join(', ')
            : '';
      }
      return { prefix: `${namePrefix} ${parsedFV.country} including`, value: states.trim() };
    case 'SHIPPING_ZIPCODES':
      parsedFV = parseSerializedFilterValue(filter.value);
      return {
        prefix: `${namePrefix} ${parsedFV.country} equal to`,
        value: `${parsedFV.parsedFilterValue?.zipcodes || ''}`,
      };
    case 'SHIPPING_COUNTRIES':
      const countriesString =
        filter.value
          ?.split(',')
          .map((countryValue) => countries[countryValue]?.name || '')
          ?.join(', ') || '';
      return { prefix: `${namePrefix}`, value: countriesString };
    default:
      return { prefix: `${namePrefix} ${filter.comparatorString}`, value: filter.value };
  }
};

// recursively builds the filter description from the Filters and FilterClauses
const getDescription = (filterOrClause: FilterOrClause): string => {
  if (isFilter(filterOrClause)) {
    const filter: Filter = filterOrClause as Filter;
    const description = formatDescriptionByFieldType(filter);
    return `${description.prefix} ${description.value}`;
  } else {
    const filterClause: FilterClause = filterOrClause as FilterClause;
    const subfilters = filterClause.filters.map(getDescription);

    if (subfilters.length === 1) return subfilters[0];

    return '(' + subfilters.join(`) ${filterClause.operator} (`) + ')';
  }
};

export const shouldUpdateName = (rule: BusinessRule) => {
  if (rule.name && rule.description) return false;

  if (rule.ruleType === RuleTypes.Insurance) return false;

  return true;
};

// sets the name and description of the rule if either are empty
export const setBusinessRuleNameAndDescription = (rule: BusinessRule) => {
  if (!shouldUpdateName(rule)) return;

  if (rule.filterGroup) {
    // default rule isn't included by default in the rules GET response, use querystring param to also retrieve it
    const fg = rule.filterGroup;

    if (!rule.name) rule.name = fg.filters.map(getName).join(' and ');
  }
};

export const generateRuleDescription = ({
  mappedServiceLevelsByToken,
  rule,
  uniqueTags,
}: {
  mappedServiceLevelsByToken?: MappedServiceLevelsToken;
  rule: BusinessRule;
  uniqueTags?: OrderTag[];
}) => {
  if (!rule || rule.description) return;
  const packageTemplate = rule.userParcelTemplate;

  let actionDescription = '';
  if (rule.ruleType === ActionType.PARCEL_TEMPLATE && packageTemplate) {
    const packageName = packageTemplate.name || packageTemplate.carrierTemplate?.name;
    actionDescription = `apply package template ${packageName}`;
  } else if (rule.ruleType === ActionType.SERVICE_LEVEL && rule.serviceLevelToken) {
    let serviceLevelName = '';
    if (
      mappedServiceLevelsByToken?.[rule.serviceLevelToken] &&
      mappedServiceLevelsByToken[rule.serviceLevelToken].length > 0
    ) {
      const serviceLevel = mappedServiceLevelsByToken[rule.serviceLevelToken][0];
      serviceLevelName = serviceLevel?.name;
    }
    if (!serviceLevelName)
      serviceLevelName = rule.serviceLevelToken.replace('_', ' ').toUpperCase();

    actionDescription = `apply preferred service level ${serviceLevelName}`;
  } else if (rule.ruleType === ActionType.SENDER_ADDRESS && rule.senderAPIAddress) {
    const addressName = rule.senderAPIAddress?.company || rule.senderAPIAddress?.name;
    actionDescription = `apply sender address ${addressName ? '"' + addressName + '"' : ''}`;
  } else if (rule.ruleType === ActionType.ASSIGN_TAG && rule.tagId && uniqueTags?.length) {
    const tag = uniqueTags?.find((tag) => tag.id === rule.tagId);
    const tagName = tag?.name || 'N/A';
    actionDescription = `apply tag '${tagName}'`;
  } else {
    return;
  }
  return rule.filterGroup?.filters
    .map(getDescription)
    .join(' and ')
    .concat(`, ${actionDescription}`);
};

export const generateReorderRulesPayload = (rules: BusinessRule[]): ReorderRulePayload[] =>
  rules.map((rule) => ({
    object_id: rule.objectId,
    rank: rule.rankDesc,
  }));

export const ruleReprocessingCompleteOrErrored = (status: string): boolean =>
  [ruleReprocessingStatus.complete, ruleReprocessingStatus.error].includes(status);

export const approximateServiceLevelNameFromToken = (token: string) =>
  token
    .split('_')
    .map((str) => str.charAt(0).toUpperCase() + str.slice(1))
    .join(' ');
